import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import {
  CenterInformationForm,
  CenterComponentsForm,
  CenterDocumentationForm,
  CenterMembershipAgreementForm
} from '../components/CenterApplication'
import {
  useCheckApplicationTokenQuery,
  useSubmitCenterApplicationMutation
} from '../redux/services/centerApplicationService'
import type { CenterApplicationFormType } from '../types'
import { toTitleCase } from '../shared/Functions'

export const CenterApplication: React.FC = () => {
  const { type, token } = useParams<{ type: string; token: string }>()
  const navigate = useNavigate()

  // Check if this is a demo route (for testing purposes)
  const isDemoRoute =
    window.location.pathname.includes('center-application-demo') &&
    (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'local')

  // API hooks
  const {
    error: tokenError,
    isError: isTokenError,
    isFetching: isTokenValidating
  } = useCheckApplicationTokenQuery(token || '', {
    skip: !token || isDemoRoute,
    // Prevent refetching on component re-mount or focus changes
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false
  })

  const [
    submitApplication,
    { isLoading: isSubmitting, isSuccess: isSubmissionSuccess }
  ] = useSubmitCenterApplicationMutation()

  // Handle token validation errors
  useEffect(() => {
    if (!isTokenValidating && isTokenError) {
      console.warn('Token validation failed:', tokenError)
      // Handle different error types
      if (tokenError && 'status' in tokenError) {
        if (tokenError.status === 404) {
          // Invalid token
          navigate('/', {
            state: {
              error:
                'Invalid application token. Please contact support@sicklecellcenters.org'
            }
          })
        } else if (tokenError.status === 409) {
          // Token already used
          navigate('/', {
            state: {
              error:
                'An application already exists that used this token. Please contact support@sicklecellcenters.org for more information'
            }
          })
        }
      } else {
        // General error
        navigate('/', {
          state: {
            error:
              'Something went wrong. Please contact support@sicklecellcenters.org'
          }
        })
      }
    }
  }, [isTokenValidating, isTokenError, tokenError, navigate])

  // Handle successful submission
  useEffect(() => {
    if (isSubmissionSuccess) {
      navigate('/', {
        state: {
          success:
            'Your application has been successfully submitted to NASCC and will be reviewed as soon as possible. Thank you!'
        }
      })
    }
  }, [isSubmissionSuccess, navigate])

  // Validation schema
  const validationSchema = Yup.object().shape({
    // Center Information
    centerName: Yup.string().required('Center name is required'),
    streetAddress: Yup.string().required('Street address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zipCode: Yup.string().required('ZIP code is required'),
    directorFirstName: Yup.string().required(
      "Director's first name is required"
    ),
    directorLastName: Yup.string().required("Director's last name is required"),
    directorEmail: Yup.string()
      .email('Please enter a valid email address')
      .required("Director's email is required"),
    coDirectorFirstName: Yup.string(),
    coDirectorLastName: Yup.string(),
    coDirectorEmail: Yup.string().email('Please enter a valid email address'),
    primaryCenterContactEmail: Yup.string()
      .email('Please enter a valid email address')
      .required('Primary contact email is required'),
    websiteAddress: Yup.string().required('Center website is required'),

    // Center Components
    numberOfDedicatedOutpatientStaff: Yup.number()
      .min(0, 'Number must be 0 or greater')
      .required('Number of dedicated outpatient staff is required'),

    // Type-dependent validation
    ...(type === 'adult' || type === 'whole_life'
      ? {
          numberOfAdultSca: Yup.number().min(0, 'Number must be 0 or greater'),
          numberOfAdultSc: Yup.number().min(0, 'Number must be 0 or greater'),
          numberOfAdultOther: Yup.number().min(0, 'Number must be 0 or greater')
        }
      : {}),

    ...(type === 'pediatric' || type === 'whole_life'
      ? {
          numberOfPediatricSca: Yup.number().min(
            0,
            'Number must be 0 or greater'
          ),
          numberOfPediatricSc: Yup.number().min(
            0,
            'Number must be 0 or greater'
          ),
          numberOfPediatricOther: Yup.number().min(
            0,
            'Number must be 0 or greater'
          )
        }
      : {}),

    // Agreements
    agreeToGrndad: Yup.boolean()
      .oneOf([true], 'You must agree to participate in GRNDAD')
      .required('You must agree to participate in GRNDAD'),
    agreeToBylaws: Yup.boolean()
      .oneOf([true], 'You must agree to the NASCC bylaws')
      .required('You must agree to the NASCC bylaws'),
    signedBy: Yup.string().required('Digital signature is required')
  })

  // Initial form values
  const initialValues: CenterApplicationFormType = {
    // Center Information
    centerName: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    directorFirstName: '',
    directorLastName: '',
    directorEmail: '',
    coDirectorFirstName: '',
    coDirectorLastName: '',
    coDirectorEmail: '',
    primaryCenterContactEmail: '',
    centerType: type || '',
    applicationToken: isDemoRoute ? 'DEMO-TOKEN' : token || '',
    websiteAddress: '',

    // Center Components
    dedicatedClinicSpace: false,
    indepClinicSpace: false,
    dcsSharedWithCancerCenter: false,
    dcsSharedWithOther: false,
    dcsSharedWithOtherName: '',
    infusionArea: false,
    infAreaDedicatedDayHospital: false,
    infAreaSharedWithCancerCenter: false,
    infAreaSharedWithOther: false,
    infAreaSharedWithOtherName: '',
    observationUnit: false,

    // Patient counts
    numberOfAdultSca: 0,
    numberOfAdultSc: 0,
    numberOfAdultOther: 0,
    numberOfPediatricSca: 0,
    numberOfPediatricSc: 0,
    numberOfPediatricOther: 0,
    numberOfDedicatedOutpatientStaff: 0,

    // Employee lists (will be implemented later when we add dynamic employee forms)
    advancedPractice: [],
    caseManager: [],
    mentalHealth: [],
    numberOfSickleCellNurses: 0,
    otherStaff: [],
    pharmacist: [],
    physicalTherapist: [],
    primaryCarePhysician: [],
    sickleCellSpecialist: [],
    socialWorker: [],

    // Documentation and protocols
    writtenProcForSickleCellDisease: false,
    emergencyDeptProtocolsForScd: false,
    opioidPolicy: false,
    infusionPolicy: false,
    trackingQualityMetricsProtocol: false,
    accessCrizanlizumabInfusions: false,
    accessPrescribeVoxeletor: false,
    accessPharmaClinicalTrials: false,
    accessStemCellTransplant: false,
    accessToCompoundHydroxyurea: false,

    // Type-specific protocols
    recNewlyTransToAdultProtocol: false,
    transToAdultProtocol: false,
    trackingNewbornScreenProtocol: false,
    pediatricHematology: false,

    // Agreements
    agreeToGrndad: false,
    agreeToBylaws: false,
    signedBy: ''
  }

  // Form submission handler
  const handleSubmit = async (values: CenterApplicationFormType) => {
    if (isDemoRoute) {
      // In demo mode, just show success after a delay
      console.log('Demo submission:', values)
      alert(
        'Demo mode: Form would be submitted with the following data:\n' +
          JSON.stringify(values, null, 2)
      )
      return
    }

    try {
      await submitApplication(values).unwrap()
      // Success is handled in useEffect
    } catch (error) {
      console.error('Submission error:', error)
      // Error handling could be added here (toast notifications, etc.)
    }
  }

  // Loading state while validating token
  if (isTokenValidating) {
    return (
      <div
        className="min-h-screen bg-[#EDE6DE] flex items-center justify-center"
        data-testid="token-validation-loading"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A6410A] mx-auto mb-4"></div>
          <p className="text-[#3C2416] text-lg">
            Validating application token...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#EDE6DE]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="text-[#A6410A] hover:text-[#894d35] font-medium flex items-center mb-4 transition-colors duration-200"
          >
            <ChevronLeftIcon className="h-5 w-5 mr-2" />
            Back to home
          </button>
          <h1 className="text-3xl font-bold text-[#3C2416] mb-2">
            {type ? toTitleCase(type) : ''} Sickle Cell Center Application
          </h1>
          <p className="text-lg text-[#142841]">
            Please complete all sections of this application form. All fields
            marked with an asterisk (*) are required.
          </p>
        </div>

        {/* Application Form */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isValid, dirty }) => (
            <Form className="space-y-8" data-testid="application-form">
              <CenterInformationForm />
              <CenterComponentsForm />
              <CenterDocumentationForm />
              <CenterMembershipAgreementForm />

              {/* Submit Button */}
              <div className="flex justify-end pt-6">
                <button
                  type="submit"
                  disabled={!isValid || !dirty || isSubmitting}
                  data-testid="submit-application-button"
                  className={`
                    px-8 py-4 rounded-full text-lg font-semibold transition-all duration-200
                    ${
                      isValid && dirty && !isSubmitting
                        ? 'bg-[#A6410A] text-white hover:bg-[#8a3608] shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }
                  `}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting Application...
                    </span>
                  ) : (
                    'Submit Application For Review'
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}
